import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { applyThemeToDocument } from '../utils/cssUtils';
import { isValidTheme } from '../utils/themeConfig';

export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

interface PersistedThemeState {
  theme: Theme;
}

const DEFAULT_THEME: Theme = 'dark';
const THEME_STORAGE_VERSION = 1;

const migrateThemeState = (persistedState: unknown): PersistedThemeState => {
  const persistedTheme =
    typeof persistedState === 'object' && persistedState !== null && 'theme' in persistedState
      ? persistedState.theme
      : undefined;

  return {
    theme:
      typeof persistedTheme === 'string' && isValidTheme(persistedTheme)
        ? persistedTheme
        : DEFAULT_THEME,
  };
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: DEFAULT_THEME,
      
      setTheme: (theme: Theme) => {
        if (!isValidTheme(theme)) {
          console.warn(`Invalid theme: ${theme}. Using default theme.`);
          theme = DEFAULT_THEME;
        }
        
        set({ theme });

        // Apply theme using CSS manager
        applyThemeToDocument(theme);
      },
      
      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme = currentTheme === 'light' ? 'dark' : 'light';
        get().setTheme(newTheme);
      },
    }),
    {
      name: 'theme-storage',
      storage: createJSONStorage<PersistedThemeState>(() => localStorage),
      version: THEME_STORAGE_VERSION,
      partialize: ({ theme }) => ({ theme }),
      migrate: migrateThemeState,
    }
  )
);

const initializeTheme = () => {
  const state = useThemeStore.getState();
  state.setTheme(state.theme);
};

// Initialize theme when module loads
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTheme);
  } else {
    initializeTheme();
  }
}
